import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LoadingBlock, ErrorState } from "@/components/ds";
import { PageTitle, Badge, money, pct } from "./_ui";

const COLS = ["Rep", "Active", "Sales", "Quota", "Quota %", "Starter", "Growth", "Pro", "Custom", "Growth Mix", "Revenue", "Base", "Bonuses", "Total Earned", "Paid"];

export default function Representatives() {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = React.useState("total_owed");
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["sales-reps"], queryFn: () => api.salesReps() });
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const reps = [...(data.reps || [])].sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));

  const sortable: Record<string, string> = { total_paid_sales: "Sales", quota_percent: "Quota %", growth_mix_percent: "Growth Mix", initial_revenue_collected: "Revenue", base_commission: "Base", total_bonuses: "Bonuses", total_owed: "Total Earned" };

  return (
    <div data-testid="sales-reps">
      <PageTitle title="Sales Representatives" subtitle="Performance for each rep's current 30-day period."
        right={
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid="reps-sort">
            {Object.entries(sortable).map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
          </select>
        } />
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
            {COLS.map((c) => <th key={c} className="px-3 py-2 whitespace-nowrap">{c}</th>)}
          </tr></thead>
          <tbody>
            {reps.length === 0 && <tr><td colSpan={COLS.length} className="px-3 py-8 text-center text-slate-400">No sales reps yet.</td></tr>}
            {reps.map((r: any) => (
              <tr key={r.user_id} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" onClick={() => navigate(`/internal/sales/reps/${r.user_id}`)} data-testid={`rep-row-${r.user_id}`}>
                <td className="px-3 py-2"><Link to={`/internal/sales/reps/${r.user_id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-blue-600 hover:underline">{r.rep_name}</Link></td>
                <td className="px-3 py-2">{r.active ? <span className="text-emerald-600">Active</span> : <span className="text-slate-400">Inactive</span>}</td>
                <td className="px-3 py-2">{r.total_paid_sales} <Badge status={r.quota_status} /></td>
                <td className="px-3 py-2">{r.quota}</td>
                <td className="px-3 py-2">{pct(r.quota_percent)}</td>
                <td className="px-3 py-2">{r.starter}</td><td className="px-3 py-2">{r.growth}</td>
                <td className="px-3 py-2">{r.pro}</td><td className="px-3 py-2">{r.custom}</td>
                <td className="px-3 py-2">{pct(r.growth_mix_percent)} <Badge status={r.growth_mix_status} /></td>
                <td className="px-3 py-2">{money(r.initial_revenue_collected)}</td>
                <td className="px-3 py-2">{money(r.base_commission)}</td>
                <td className="px-3 py-2">{money(r.total_bonuses)}</td>
                <td className="px-3 py-2 font-semibold">{money(r.total_owed)}</td>
                <td className="px-3 py-2">{r.outstanding > 0 ? <span className="text-amber-600">{money(r.total_paid)}</span> : <span className="text-emerald-600">{money(r.total_paid)}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
